<template>
    <a-modal :open="open" :title="title" :width="360" centered :footer="null" :mask-closable="false" :keyboard="false"
        @cancel="handleCancel">
        <div class="verify-slider">
            <div class="verify-img-panel" :style="{ width: imgWidth + 'px', height: imgHeight + 'px' }">
                <img v-if="backImgBase" class="verify-bg-img" :src="'data:image/png;base64,' + backImgBase" alt="验证码底图" />
                <img v-if="blockImgBase" class="verify-block-img" :src="'data:image/png;base64,' + blockImgBase"
                    alt="拼图块" :style="{ left: moveBlockLeft + 'px', width: blockWidth + 'px', height: imgHeight + 'px' }" />
                <div v-if="loadingImg" class="verify-loading">加载中...</div>
                <div class="verify-refresh" @click="refresh">刷新</div>
                <span v-if="tipWords" class="verify-tips" :class="passFlag ? 'suc-bg' : 'err-bg'">{{ tipWords }}</span>
            </div>
            <div class="verify-bar-area" @selectstart.prevent
                :style="{ width: imgWidth + 'px', height: barHeight + 'px' }">
                <div class="verify-track"
                    :style="{ width: (moveBlockLeft + barHeight) + 'px', borderColor: trackBorderColor, backgroundColor: trackBgColor }">
                </div>
                <span class="verify-msg">{{ text }}</span>
                <div class="verify-move-block" @mousedown="start" @touchstart="start"
                    :style="{ left: moveBlockLeft + 'px', width: barHeight + 'px', height: barHeight + 'px', backgroundColor: moveBgColor }">
                    <span class="verify-icon">{{ iconText }}</span>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { aesEncrypt, buildCaptchaVerification, captchaGet, captchaCheck } from '@/utils/captcha';

export default {
    name: 'VerifySlider',
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '请完成滑动验证',
        },
        captchaType: {
            type: String,
            default: 'blockPuzzle',
        },
    },
    emits: ['update:open', 'success', 'cancel'],
    data() {
        return {
            // 后端底图固定 310x155、拼图块 47x155，显示尺寸与之一致，拖动位移即后端坐标 x
            imgWidth: 310,
            imgHeight: 155,
            barHeight: 40,
            blockWidth: 47,
            backImgBase: '',
            blockImgBase: '',
            backToken: '',
            secretKey: '',
            loadingImg: true,
            text: '向右滑动完成验证',
            tipWords: '',
            passFlag: false,
            status: false,
            isEnd: false,
            moveBlockLeft: 0,
            startLeft: 0,
            trackBgColor: 'transparent',
            trackBorderColor: '#ddd',
            moveBgColor: '#fff',
            iconText: '→',
        };
    },
    watch: {
        // 每次打开都重新获取一次验证码（token 一次性，不复用）
        open(value) {
            if (value) {
                this.refresh();
            }
        },
    },
    methods: {
        // 复位交互状态
        reset() {
            this.status = false;
            this.isEnd = false;
            this.moveBlockLeft = 0;
            this.text = '向右滑动完成验证';
            this.tipWords = '';
            this.passFlag = false;
            this.trackBgColor = 'transparent';
            this.trackBorderColor = '#ddd';
            this.moveBgColor = '#fff';
            this.iconText = '→';
        },
        // 刷新验证码
        async refresh() {
            this.reset();
            this.backImgBase = '';
            this.blockImgBase = '';
            this.backToken = '';
            this.loadingImg = true;
            try {
                const data = await captchaGet(this.captchaType);
                this.backImgBase = data.originalImageBase64;
                this.blockImgBase = data.jigsawImageBase64;
                this.backToken = data.token;
                this.secretKey = data.secretKey || '';
            } catch (e) {
                this.tipWords = e && e.message ? e.message : '验证码加载失败，请刷新重试';
            } finally {
                this.loadingImg = false;
            }
        },
        // 按下滑块，记录光标与滑块的相对位置
        start(e) {
            if (this.isEnd || this.loadingImg || !this.backToken) {
                return;
            }
            e.preventDefault();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            this.startLeft = clientX - this.moveBlockLeft;
            this.status = true;
            this.text = '';
            this.moveBgColor = '#337ab7';
            this.trackBorderColor = '#337ab7';
            this.trackBgColor = '#e6f7ff';
            window.addEventListener('mousemove', this.move);
            window.addEventListener('mouseup', this.end);
            window.addEventListener('touchmove', this.move);
            window.addEventListener('touchend', this.end);
        },
        // 拖动中，位移限制在滑道内
        move(e) {
            if (!this.status || this.isEnd) {
                return;
            }
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const maxLeft = this.imgWidth - this.barHeight;
            let left = clientX - this.startLeft;
            if (left < 0) {
                left = 0;
            }
            if (left > maxLeft) {
                left = maxLeft;
            }
            this.moveBlockLeft = left;
        },
        // 松手提交校验，成功后本地生成一次性凭证
        end() {
            this.removeListeners();
            if (!this.status || this.isEnd) {
                return;
            }
            this.status = false;
            // 位移即后端坐标 x（图片按官方基准 310px 渲染，无需换算），y 固定 5.0
            const plain = JSON.stringify({ x: Math.round(this.moveBlockLeft), y: 5.0 });
            const pointJson = aesEncrypt(plain, this.secretKey);
            // 凭证必须在成功回调里本地算好：组件下次刷新就丢了，且后端响应里该字段恒为 null
            const captchaVerification = buildCaptchaVerification(this.backToken, plain, this.secretKey);
            captchaCheck(this.captchaType, this.backToken, pointJson)
                .then(() => {
                    this.isEnd = true;
                    this.passFlag = true;
                    this.moveBgColor = '#5cb85c';
                    this.trackBorderColor = '#5cb85c';
                    this.trackBgColor = '#d9f7be';
                    this.iconText = '✓';
                    this.tipWords = '验证通过';
                    setTimeout(() => {
                        // 期间被取消则不再触发登录
                        if (this.isEnd) {
                            this.$emit('success', captchaVerification);
                        }
                    }, 300);
                })
                .catch(err => {
                    this.passFlag = false;
                    this.moveBgColor = '#d9534f';
                    this.trackBorderColor = '#d9534f';
                    this.iconText = '×';
                    this.tipWords = err && err.message ? err.message : '验证失败，请重试';
                    setTimeout(() => {
                        this.refresh();
                    }, 1000);
                });
        },
        removeListeners() {
            window.removeEventListener('mousemove', this.move);
            window.removeEventListener('mouseup', this.end);
            window.removeEventListener('touchmove', this.move);
            window.removeEventListener('touchend', this.end);
        },
        // 取消验证：不发登录请求，交由父组件复位
        handleCancel() {
            this.removeListeners();
            this.reset();
            this.$emit('update:open', false);
            this.$emit('cancel');
        },
    },
    beforeUnmount() {
        this.removeListeners();
    },
};
</script>

<style scoped>
.verify-slider {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.verify-img-panel {
    position: relative;
    overflow: hidden;
    border-radius: 4px;
}

.verify-bg-img,
.verify-block-img {
    display: block;
}

.verify-bg-img {
    width: 100%;
    height: 100%;
}

.verify-block-img {
    position: absolute;
    top: 0;
}

.verify-loading {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0a5aa;
    font-size: 13px;
    background: #f7f9fa;
}

.verify-refresh {
    position: absolute;
    right: 4px;
    top: 4px;
    padding: 1px 6px;
    font-size: 12px;
    color: #fff;
    background: rgba(0, 0, 0, .35);
    border-radius: 3px;
    cursor: pointer;
}

.verify-tips {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    line-height: 22px;
    text-align: center;
    font-size: 12px;
    color: #fff;
    background: rgba(0, 0, 0, .5);
}

.verify-tips.suc-bg {
    background: rgba(92, 184, 92, .9);
}

.verify-tips.err-bg {
    background: rgba(217, 83, 79, .9);
}

.verify-bar-area {
    position: relative;
    margin-top: 5px;
    box-sizing: border-box;
    background: #f7f9fa;
    border: 1px solid #e4e7eb;
    border-radius: 4px;
    text-align: center;
    user-select: none;
    overflow: hidden;
}

.verify-msg {
    line-height: 38px;
    font-size: 13px;
    color: #a0a5aa;
}

.verify-track {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    box-sizing: border-box;
    border-right: 1px solid #ddd;
}

.verify-move-block {
    position: absolute;
    top: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 0 3px rgba(0, 0, 0, .15);
    cursor: move;
}

.verify-icon {
    font-size: 16px;
    color: #34495e;
}
</style>
